import { useContext, useEffect, useState } from "react";
import { Calendar, Spin, Button, Space } from "antd";
import { RouteContext } from "../NewRoute";
import {
  useGetInvoicesForRangeIntersectQuery,
  useGetRoutesForMonthQuery,
} from "../../../app/services/api";
import { useFirestore } from "react-redux-firebase";
import moment from "moment";
import { SecondsToDate } from "../../../app/utils/time/secondsToDate";
import TextSubtitle from "../../../app/system-components/typography/text/TextSubtitle";
import {
  COLOR_GREEN_0,
  COLOR_YELLOW_0,
} from "../../../app/ui/colorConstants";
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import {
  RECEIVE_METHOD_DELIVERY,
  RECEIVE_METHOD_PICKUP,
  RETURN_METHOD_DROPOFF,
  RETURN_METHOD_ONSITE,
} from "../../../app/utils/models/modelConstants/modelConstants";
import { firelikeId } from "../../../app/utils/models/checkers/firelikeid";
import { useSelector } from "react-redux";
import { authSelector } from "../../auth/authSlice";

const LOCAL_STORAGE_KEY = "rentkit_calendar_selected_date";

const SelectDate = () => {
  const firestore = useFirestore();
  const { fsOrgPrefix, orgData } = useSelector(authSelector);

  const [value, setValue] = useState(() => {
    const savedDate = localStorage.getItem(LOCAL_STORAGE_KEY);
    return savedDate ? moment(savedDate) : moment();
  });

  const { state, actions } = useContext(RouteContext);

  // Calculate month start/end based on the controlled value
  const monthStart = value.clone().startOf("month");
  const monthEnd = value.clone().endOf("month");

  const {
    data,
    isFetching: fetchingRoutes,
    refetch: refetchRoutes,
  } = useGetRoutesForMonthQuery({
    db: { firestore },
    orgPrefix: fsOrgPrefix,
    monthStart: monthStart,
    monthEnd: monthEnd,
    orgTimezone: orgData?.orgTimezone,
  });

  const {
    data: invoices,
    isFetching: fetchingInvoices,
    refetch: refetchInvoices,
  } = useGetInvoicesForRangeIntersectQuery({
    db: { firestore },
    orgPrefix: fsOrgPrefix,
    dateStart: monthStart,
    dateEnd: monthEnd,
    orgTimezone: orgData?.orgTimezone,
    useTurnaround: false,
    invoiceOnly: true,
  });

  // Save selected date to local storage on change
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, value.toISOString());
  }, [value]);

  const onPanelChange = (newValue) => {
    setValue(newValue);
  };

  const handlePrevMonth = () => {
    setValue(value.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setValue(value.clone().add(1, "month"));
  };

  const handleToday = () => {
    setValue(moment());
  };

  const dateCellRender = (cell) => {
    const cellMoment = moment(cell); // Ensure we work with moment objects
    const cellStart = cellMoment.clone().startOf("day");
    const cellEnd = cellMoment.clone().endOf("day");

    const inDay = data?.filter((route) => {
      const routeDay = moment(SecondsToDate(route?.routeDate));
      // Ensure comparison is done with moment objects
      const beforeEnd = cellEnd.isSameOrAfter(routeDay, "day");
      const afterStart = cellStart.isSameOrBefore(routeDay, "day");
      return afterStart && beforeEnd;
    });

    const invoicesInDay = invoices?.filter((invoice) => {
      const invoiceStart = moment(SecondsToDate(invoice?.rentalDateStart));
      const invoiceEnd = moment(SecondsToDate(invoice?.rentalDateEnd));

      // Check if delivery is needed on this day
      const hasDeliveryToday =
        invoice?.receiveMethod === RECEIVE_METHOD_DELIVERY &&
        cellStart.isSame(invoiceStart, "day"); // Use isSame for day comparison

      // Check if onsite pickup is needed on this day
      const hasOnSitePickupToday =
        invoice?.returnMethod === RETURN_METHOD_ONSITE &&
        cellEnd.isSame(invoiceEnd, "day"); // Use isSame for day comparison

      // Return true only if there's actual company action needed on this day
      return hasDeliveryToday || hasOnSitePickupToday;
    });

    const completed = invoicesInDay?.filter((i) => {
      return inDay?.find((r) => {
        // Ensure invoice ID exists before checking includes
        return r?.invoicesQueryHook?.includes(i.id);
      });
    });

    // Handle division by zero
    const isComplete =
      invoicesInDay?.length > 0 &&
      completed?.length / invoicesInDay?.length === 1;

    return (
      <div
        key={cellMoment.format("YYYY-MM-DD")} // Use a consistent key
        onClick={() => actions.onSelectDay(cellMoment)} // Pass moment object
        style={{ height: "100%" }}
      >
        {invoicesInDay?.length > 0 && !fetchingRoutes && !fetchingInvoices && (
          <div>
            Orders Routed:{" "}
            <span
              style={{
                fontWeight: "bold",
                color: isComplete ? COLOR_GREEN_0 : COLOR_YELLOW_0,
              }}
            >
              {completed?.length}/{invoicesInDay?.length}
            </span>
          </div>
        )}

        {inDay?.map((route) => {
          return (
            // Use route ID for key if available and unique, otherwise generate one
            <div key={route.id || firelikeId()}>
              {route?.isComplete ? (
                <CheckCircleFilled
                  style={{ color: COLOR_GREEN_0, marginRight: "4px" }}
                />
              ) : (
                <WarningFilled
                  style={{ color: COLOR_YELLOW_0, marginRight: "4px" }}
                />
              )}
              <TextSubtitle text={route?.name} />
              <br />
            </div>
          );
        })}
      </div>
    );
  };

  const monthCellRender = (m) => {};

  const onSelect = (newValue) => {
    // Update the main value state as well when a date is selected
    setValue(newValue);
    actions.setDate({
      string: newValue.format("MMM Do YYYY"),
      moment: newValue,
    });
  };

  return (
    <div>
      <Spin spinning={fetchingRoutes || fetchingInvoices}>
        <div style={{ padding: "12px", borderBottom: "1px solid #f0f0f0", display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: "24px" }}>
          <div>
            <Button style={{ border: '1px solid #d9d9d9', marginRight: '8px', backgroundColor: '#e6f7ff', borderColor: '#91d5ff' }}>
              Currently viewing: {value.format("MMMM YYYY")}
            </Button>
          </div>
          <Space>
            <Button onClick={handlePrevMonth}>{"<"}</Button>
            <Button onClick={handleToday}>Today</Button>
            <Button onClick={handleNextMonth}>{">"}</Button>
          </Space>
        </div>
        <Calendar
          value={value} // Control the calendar value
          style={{ padding: "12px" }}
          onSelect={onSelect}
          onPanelChange={onPanelChange}
          dateCellRender={dateCellRender}
          monthCellRender={monthCellRender} // Keep this even if empty for consistency
        />
      </Spin>
    </div>
  );
};

export default SelectDate;
